import type { ChartGroup } from '@integratio/core';

export interface DetailPage {
  columns: [ChartGroup[], ChartGroup[], ChartGroup[]];
}

/**
 * Pesos aproximados (unidades de altura) calibrados para a folha A4 paisagem
 * do relatório (297x210mm, ~150mm úteis por coluna após header/footer/paddings).
 * Cada unidade corresponde grosseiramente à altura de uma linha de item.
 */
const HEADER_WEIGHT = 1.6;
const ITEM_WEIGHT = 1;
const EMPTY_WEIGHT = 1.3;

/**
 * Capacidade de uma coluna por página, já com margem de segurança.
 * Em 3 colunas, a largura menor aumenta as quebras de linha dos detalhes,
 * então reduzimos a capacidade para evitar corte visual na folha.
 */
const COLUMN_CAPACITY = 12;

function groupWeight(group: ChartGroup): number {
  if (group.count === 0) return HEADER_WEIGHT + EMPTY_WEIGHT;
  return HEADER_WEIGHT + group.stakeholders.length * ITEM_WEIGHT;
}

/**
 * Divide um grupo muito grande (que sozinho excederia a capacidade de uma
 * coluna) em "pedaços" menores, repetindo o cabeçalho com "(cont.)" nos
 * pedaços subsequentes. Evita que um único grupo grande seja cortado.
 */
function chunkGroup(group: ChartGroup): ChartGroup[] {
  if (group.count === 0) return [group];
  const maxItemsPerChunk = Math.max(1, Math.floor((COLUMN_CAPACITY - HEADER_WEIGHT) / ITEM_WEIGHT));
  if (group.stakeholders.length <= maxItemsPerChunk) return [group];

  const chunks: ChartGroup[] = [];
  for (let i = 0; i < group.stakeholders.length; i += maxItemsPerChunk) {
    const slice = group.stakeholders.slice(i, i + maxItemsPerChunk);
    chunks.push({
      ...group,
      key: `${group.key}__chunk${i}`,
      name: i === 0 ? group.name : `${group.name} (cont.)`,
      stakeholders: slice,
    });
  }
  return chunks;
}

/**
 * Divide os grupos em páginas de detalhamento (3 colunas cada), garantindo
 * que nenhuma coluna exceda a capacidade estimada da folha A4 paisagem.
 * Nunca corta um grupo sem antes fragmentá-lo explicitamente (chunkGroup),
 * evitando que nomes sejam cortados pelo overflow:hidden da folha.
 */
export function paginateGroups(groups: ChartGroup[]): DetailPage[] {
  const chunked = groups.flatMap(chunkGroup);
  const pages: DetailPage[] = [];
  let columns: [ChartGroup[], ChartGroup[], ChartGroup[]] = [[], [], []];
  let weights: [number, number, number] = [0, 0, 0];

  const flush = (): void => {
    if (columns[0].length || columns[1].length || columns[2].length) pages.push({ columns });
    columns = [[], [], []];
    weights = [0, 0, 0];
  };

  for (const group of chunked) {
    const weight = groupWeight(group);
    let target = 0;
    if (weights[1] < weights[target]) target = 1;
    if (weights[2] < weights[target]) target = 2;
    const currentWeight = weights[target];

    if (currentWeight + weight > COLUMN_CAPACITY && (columns[0].length || columns[1].length || columns[2].length)) {
      flush();
      target = 0;
    }

    columns[target].push(group);
    weights[target] += weight;
  }

  flush();
  return pages.length ? pages : [{ columns: [[], [], []] }];
}

/**
 * Divide uma lista plana em blocos de tamanho fixo (usado para a tabela
 * resumida, paginando por linhas sem cortar nenhuma entre páginas).
 */
export function chunkRows<T>(rows: T[], perPage: number): T[][] {
  if (rows.length === 0) return [[]];
  const chunks: T[][] = [];
  for (let i = 0; i < rows.length; i += perPage) {
    chunks.push(rows.slice(i, i + perPage));
  }
  return chunks;
}
